import React, { useState, useEffect } from "react";
import UploadIcon from "../../../../assets/images/upload-icon.svg";
import type { UploadProps } from "antd";
import { Upload, message, Modal, Progress } from "antd";
import { Storage } from "aws-amplify";
import { fileUploadProps } from "./constants";
import { FileTypes } from "../../../../enums/FileUploadEnums";
import pdfIcon from "../../../../assets/images/view_pdf.svg";
import styles from "./CustomerApproval.module.scss";
import deleteIcon from "../../../../assets/images/delete-icon.svg";
import CreateJobServices from "../../../../services/createJob.services";
import { UploadMessages } from "../../../../enums/UploadDraggerMessagesEnums";
import { getUploadFileTypeHint } from "../../../../utils/textFormatUtils";

const CustomerApprovalUploadDragger = ({
  handleFileUpload,
  fileType,
  listOfUploadedFiles,
  maxFileSize,
  allowedFileTypes = [],
  leadId,
}) => {
  const { DeleteDocuments } = CreateJobServices();
  const { mutateAsync: deleteDocuments } = DeleteDocuments();
  const { Dragger } = Upload;
  const {
    maxFileCount,
    defaultSheetStatus,
    fileUploadSuccessMessage,
    fileSizeWarning
  } = fileUploadProps;
  const [fileList, setFileList] = useState(
    listOfUploadedFiles?.length ? listOfUploadedFiles : []
  );
  const [newFileList, setnewFileList] = useState([]);

  const handleUpload = async (file, onSuccess, onError, onProgress) => {
    try {
      const timestampMs = new Date().getTime();
      const fileName = `${timestampMs}/${file.name}`;
      const result = await Storage.put(fileName, file, {
        contentType: file.type,
        progressCallback: (progress) => {
          const percent = Math.round((progress.loaded / progress.total) * 100);
          onProgress({ percent });
        },
      });
      const fileURL = await Storage.get(result.key, { level: "public" });

      const newFile = {
        name: file.name,
        docAttachment: fileURL?.split("?")[0],
        docAttachmentId: file.uid,
        fileType: file.type,
        sheetStatus: defaultSheetStatus,
        docType: fileType,
        document: fileType,
        signatureVerified: true,
      };
      message.success(`${file.name} uploaded successfully`);
      onSuccess(fileUploadSuccessMessage);
      setnewFileList((prev) => [...prev, newFile]);
      handleFileUpload([...newFileList, newFile]);
    } catch (error) {
      message.error(`${file.name} upload failed`);
      onError(error);
    }
  };
  const maxCount = maxFileSize ? maxFileSize : maxFileCount;
  const handleFileRemove = async (file) => {
    const isOriginalFile = listOfUploadedFiles?.some(
      (original) => original?.id === file?.id
    );

    if (isOriginalFile) {
      Modal.info({
        title: "Protected File",
        content: "This file was previously uploaded and cannot be deleted.",
      });
      return;
    }
    if (file?.id) {
      await deleteDocuments({
        recordIds: [file?.id],
        leadId: leadId,
      });
    }
    const updatedFileList = fileList.filter((item) => item.uid !== file.uid);
    setFileList(updatedFileList);
    const updatedNewFileList = newFileList.filter(
      (item) => item.docAttachmentId !== file.uid
    );
    setnewFileList(updatedNewFileList);
    handleFileUpload(updatedNewFileList);
  };
  const props: UploadProps = {
    defaultFileList: listOfUploadedFiles,
    fileList,
    maxCount: maxCount,
    beforeUpload: async (file) => {
      const isDuplicate = fileList.some(
        (existing) => existing.name === file.name
      );

      if (isDuplicate) {
        return new Promise((resolve) => {
          Modal.confirm({
            title: "Duplicate File",
            content: `${file.name} is already uploaded. Do you want to upload it again?`,
            onOk: () => {
              resolve(true); // Allow upload
            },
            onCancel: () => {
              resolve(Upload.LIST_IGNORE); // Ignore file
            },
            onProgress: (progress) => {},
          });
        });
      }
      if (!!!allowedFileTypes?.length && file.type !== FileTypes.PDF) {
        message.error("Only PDF files are allowed.");
        return Upload.LIST_IGNORE; // Prevent adding non-PDF files
      }
      if (!!allowedFileTypes?.length && !allowedFileTypes.includes(file.type)) {
        message.error("Only png files are allowed.");
        return Upload.LIST_IGNORE; // Prevent adding non-PDF files
      }

      if (file.size > 10 * 1024 * 1024) {
        message.error(UploadMessages.LESS_THAN_10_MB);
        return Upload.LIST_IGNORE; // Prevent adding large files
      }
      return true;
    },
    headers: {
      authorization: "authorization-text",
    },
    customRequest: ({ file, onSuccess, onError, onProgress }) =>
      handleUpload(file, onSuccess, onError, onProgress),
    onChange(info) {
      setFileList(info.fileList);
    },
    itemRender: (originNode, file) => {
      const url = (file as any).docAttachment;
      const isUploading = file.status === "uploading";
      return (
        <div className={`${styles.FileItem}`}>
          <div className={styles.LeftContent}>
            <div className={styles.LeftGroup}>
              <img src={pdfIcon} alt="PDF" />
              {isUploading ? (
                <span>{file.name}</span>
              ) : (
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {file.name}
                </a>
              )}
            </div>
            {isUploading && (
              <Progress
                percent={Math.round(file.percent)}
                size="small"
                className={styles.UploadProgress}
                strokeColor={fileUploadProps.PROGRESS_COLOR}
              />
            )}
            {file.status === "error" && (
              <span className={styles.FileError}>Upload Failed</span>
            )}
          </div>
          {!isUploading && (
            <img
              src={deleteIcon}
              className={styles.DeleteIcon}
              alt="Delete Icon"
              onClick={() => handleFileRemove(file)}
            />
          )}
        </div>
      );
    },
  };

  useEffect(() => {
    setFileList(listOfUploadedFiles ?? []);
  }, [listOfUploadedFiles]);

  return (
    <div>
      <Dragger {...props}>
        <p className={styles.UploadIconContainer}>
          <img src={UploadIcon} alt="upload icon" />
        </p>
        <p className="ant-upload-text">
          Click or drag file to this area to upload
        </p>
        <p className="ant-upload-hint">{fileSizeWarning} {getUploadFileTypeHint(!!allowedFileTypes?.length ? allowedFileTypes : [FileTypes?.PDF])}</p>
      </Dragger>
    </div>
  );
};

export default CustomerApprovalUploadDragger;
